// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminCampaigns\AdminCampaignDetail.jsx
// Название: Admin Campaign Detail View
// Версия: 2.0.0
// Дата: 01.02.2026

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './AdminCampaignDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const AdminCampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    loadCampaign();
  }, [id]);

  const loadCampaign = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setCampaign(data.data);
        setEditForm({
          name: data.data.name || '',
          description: data.data.description || '',
          custom_message: data.data.custom_message || '',
          budget_dar: data.data.budget_dar || 0,
          reward_amount: data.data.reward_amount || 0
        });
      }
    } catch (err) {
      console.error('Load campaign error:', err);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      const data = await res.json();
      if (data.success) {
        setEditing(false);
        loadCampaign();
      } else {
        alert(data.error || 'Ошибка сохранения');
      }
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const endpoint = newStatus === 'cancelled' ? 'cancel' : 'complete';
    const confirmMsg = newStatus === 'cancelled' ? 'Отменить кампанию?' : 'Завершить кампанию?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns/${id}/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        loadCampaign();
      } else {
        alert(data.error || 'Ошибка');
      }
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleDeleteGift = async (giftId) => {
    if (!window.confirm('Удалить подарок?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/gifts/${giftId}?force=true`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        loadCampaign();
      } else {
        alert(data.error || 'Ошибка удаления');
      }
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      draft: { label: 'Черновик', className: 'badge-secondary' },
      ready: { label: 'Готова', className: 'badge-primary' },
      active: { label: 'Активна', className: 'badge-success' },
      generating: { label: 'Генерация', className: 'badge-warning' },
      completed: { label: 'Завершена', className: 'badge-purple' },
      cancelled: { label: 'Отменена', className: 'badge-danger' },
      pending: { label: 'Ожидает', className: 'badge-secondary' },
      claimed: { label: 'Получен', className: 'badge-success' },
      expired: { label: 'Истёк', className: 'badge-warning' },
      failed: { label: 'Ошибка', className: 'badge-danger' }
    };
    return map[status] || { label: status || '-', className: 'badge-secondary' };
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('ru-RU') : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('ru-RU') : '-';

  if (loading) {
    return <div className="campaign-detail"><div className="loading-state">⏳ Загрузка...</div></div>;
  }

  if (!campaign) {
    return <div className="campaign-detail"><div className="error-state">❌ Кампания не найдена</div></div>;
  }

  const status = getStatusBadge(campaign.status);
  const claimRate = campaign.gifts_count > 0 
    ? Math.round((campaign.claimed_count / campaign.gifts_count) * 100) 
    : 0;

  return (
    <div className="campaign-detail">
      {/* Header */}
      <div className="detail-header">
        <button className="btn-back" onClick={() => navigate('/admin/campaigns')}>← Назад</button>
        <div className="header-info">
          <h1>#{campaign.id} {campaign.name || 'Без названия'}</h1>
          <div className="header-meta">
            <span className="badge badge-purple">🎨 Brand Giveaway</span>
            <span className={`badge ${status.className}`}>{status.label}</span>
            <span className="meta-text">{campaign.campaign_type || 'brand_giveaway'}</span>
          </div>
        </div>
        <div className="header-actions">
          {!editing && !['completed', 'cancelled'].includes(campaign.status) && (
            <>
              <button className="btn-action-header btn-edit" onClick={() => setEditing(true)}>✏️</button>
              <button className="btn-action-header btn-complete" onClick={() => handleStatusChange('completed')}>✅</button>
              <button className="btn-action-header btn-cancel-action" onClick={() => handleStatusChange('cancelled')}>⛔</button>
            </>
          )}
          {editing && (
            <>
              <button className="btn-action-header btn-save" onClick={handleSave}>💾</button>
              <button className="btn-action-header btn-cancel-edit" onClick={() => setEditing(false)}>✖️</button>
            </>
          )}
          <button className="btn-action-header btn-refresh" onClick={loadCampaign}>🔄</button>
        </div>
      </div>

      {/* Stats */}
      <div className="detail-stats">
        <div className="stat-box">
          <span className="stat-value">{campaign.gifts_count || 0}</span>
          <span className="stat-label">Подарков</span>
        </div>
        <div className="stat-box green">
          <span className="stat-value">{campaign.claimed_count || 0}</span>
          <span className="stat-label">Claimed</span>
        </div>
        <div className="stat-box blue">
          <span className="stat-value">{claimRate}%</span>
          <span className="stat-label">Claim Rate</span>
        </div>
        <div className="stat-box orange">
          <span className="stat-value">{parseFloat(campaign.budget_dar || 0).toFixed(0)}</span>
          <span className="stat-label">Бюджет DAR</span>
        </div>
        <div className="stat-box purple">
          <span className="stat-value">{parseFloat(campaign.reward_amount || 0).toFixed(0)}</span>
          <span className="stat-label">DAR/подарок</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button className={activeTab === 'info' ? 'active' : ''} onClick={() => setActiveTab('info')}>
          📋 Информация
        </button>
        <button className={activeTab === 'gifts' ? 'active' : ''} onClick={() => setActiveTab('gifts')}>
          🎁 Подарки ({campaign.gifts?.length || 0})
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'info' && (
          <div className="info-tab">
            {editing ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>Название</label>
                  <input 
                    type="text" 
                    value={editForm.name} 
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Описание</label>
                  <textarea 
                    value={editForm.description} 
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>Сообщение</label>
                  <textarea 
                    value={editForm.custom_message} 
                    onChange={(e) => setEditForm({...editForm, custom_message: e.target.value})}
                    rows={2}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Бюджет DAR</label>
                    <input 
                      type="number" 
                      value={editForm.budget_dar} 
                      onChange={(e) => setEditForm({...editForm, budget_dar: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Сумма подарка</label>
                    <input 
                      type="number" 
                      value={editForm.reward_amount} 
                      onChange={(e) => setEditForm({...editForm, reward_amount: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="info-grid">
                <div className="info-section">
                  <h3>📋 Основное</h3>
                  <div className="info-row"><span>Тип:</span><span>{campaign.campaign_type || 'brand_giveaway'}</span></div>
                  <div className="info-row"><span>Статус:</span><span className={`badge ${status.className}`}>{status.label}</span></div>
                  <div className="info-row"><span>Описание:</span><span>{campaign.description || '-'}</span></div>
                  <div className="info-row"><span>Сообщение:</span><span>{campaign.custom_message || '-'}</span></div>
                </div>
                <div className="info-section">
                  <h3>🏢 Бизнес</h3>
                  <div className="info-row"><span>ID:</span><span>#{campaign.business_id}</span></div>
                  <div className="info-row"><span>Компания:</span><span>{campaign.company_name || campaign.business_name || '-'}</span></div>
                  <div className="info-row"><span>Email:</span><span>{campaign.business_email}</span></div>
                </div>
                <div className="info-section">
                  <h3>💰 Финансы</h3>
                  <div className="info-row"><span>Бюджет:</span><span><strong>{parseFloat(campaign.budget_dar || 0).toFixed(0)} DAR</strong></span></div>
                  <div className="info-row"><span>Сумма подарка:</span><span>{parseFloat(campaign.reward_amount || 0).toFixed(0)} DAR</span></div>
                  <div className="info-row"><span>Использовано:</span><span>{parseFloat(campaign.total_dar || 0).toFixed(0)} DAR</span></div>
                </div>
                <div className="info-section">
                  <h3>📅 Даты</h3>
                  <div className="info-row"><span>Создана:</span><span>{formatDateTime(campaign.created_at)}</span></div>
                  <div className="info-row"><span>Обновлена:</span><span>{formatDateTime(campaign.updated_at)}</span></div>
                  {campaign.start_date && <div className="info-row"><span>Начало:</span><span>{formatDate(campaign.start_date)}</span></div>}
                  {campaign.end_date && <div className="info-row"><span>Окончание:</span><span>{formatDate(campaign.end_date)}</span></div>}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'gifts' && (
          <div className="gifts-tab">
            {campaign.gifts?.length > 0 ? (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Код</th>
                      <th>Сумма</th>
                      <th>Статус</th>
                      <th>Получатель</th>
                      <th>Claimed</th>
                      <th>Изображение</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaign.gifts.map(g => {
                      const gStatus = getStatusBadge(g.status);
                      return (
                        <tr key={g.id} className={g.status === 'claimed' ? 'row-claimed' : ''}>
                          <td className="id-cell">#{g.id}</td>
                          <td className="code-cell">{g.short_code || g.gift_id?.slice(0, 8) || '-'}</td>
                          <td>{parseFloat(g.amount || 0).toFixed(0)} DAR</td>
                          <td><span className={`badge ${gStatus.className}`}>{gStatus.label}</span></td>
                          <td className="email-cell">{g.claimed_by_email || '-'}</td>
                          <td className="date-cell">{g.claimed_at ? formatDateTime(g.claimed_at) : '-'}</td>
                          <td>
                            {g.image_url ? (
                              <a href={g.image_url} target="_blank" rel="noreferrer" className="img-link">🖼️ Открыть</a>
                            ) : '-'}
                          </td>
                          <td className="actions-cell">
                            {g.status !== 'claimed' && (
                              <button className="btn-action btn-delete" onClick={() => handleDeleteGift(g.id)} title="Удалить">🗑️</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">Нет подарков в этой кампании</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCampaignDetail;